"use client";
import { FC, Fragment, ReactNode, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { PauseVideo, PlayVideo, Spinner, XMarkThick } from "@/icons";
import { Dialog, DialogPanel, Transition } from "@headlessui/react";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import { ComponentWithAnalyticsGlobalField } from "@solarwinds-webbiz/types";
import type {
  WistiaPlayerElement,
  WistiaPlayerProps as WistiaProps,
} from "@wistia/wistia-player-react";

const WistiaMediaPlayer = dynamic(
  () =>
    import("@wistia/wistia-player-react").then((mod) => ({
      default: mod.WistiaPlayer,
    })),
  {
    ssr: false,
  },
);

type WistiaPlayerProps = {
  uuid: string;
  dld?: string;
  dlt?: string;
  link?: ReactNode;
  children?: ReactNode | ReactNode[];
  video_play_pause_button_analytics?: ComponentWithAnalyticsGlobalField;
  video_play_button_label?: string;
  video_pause_button_label?: string;
  autoplay?: boolean;
  silent_auto_play?: boolean;
  aspect_ratio?: number;
} & Pick<WistiaProps, "onLoadedMediaData">;

const DEFAULT_ASPECT_RATIO = 16 / 9;

const WistiaPlayer: FC<WistiaPlayerProps> = ({
  uuid,
  dld,
  dlt,
  link,
  children,
  video_play_pause_button_analytics,
  video_play_button_label,
  video_pause_button_label,
  autoplay,
  silent_auto_play,
  aspect_ratio,
  onLoadedMediaData,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [paused, setPaused] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const playerRef = useRef<WistiaPlayerElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    // Don't lazy load if modal-based
    if (!container || link) return;
    // First: Lazy load when container is visible
    if (!shouldLoad) {
      const loadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setShouldLoad(true);
              loadObserver.disconnect();
            }
          });
        },
        {
          threshold: 0,
        },
      );
      loadObserver.observe(container);
      return () => loadObserver.disconnect();
      // Second: Once loaded, setup autoplay observer
    } else if (autoplay && playerRef.current) {
      const currentVideo = playerRef.current;
      const playObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              currentVideo.play();
            } else {
              currentVideo.pause();
            }
          });
        },
        {
          threshold: 0.5,
        },
      );
      playObserver.observe(currentVideo);
      return () => {
        playObserver.unobserve(currentVideo);
      };
    }
  }, [shouldLoad, link, autoplay]);

  const closeModal = async () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
    setLoading(true);
    setShouldLoad(true);
  };

  const pauseButtonHandler = () => {
    if (playerRef.current) {
      playerRef.current.pause();
      setPaused(true);
    }
  };

  const playButtonHandler = () => {
    if (playerRef.current) {
      playerRef.current.play();
      setPaused(false);
    }
  };

  const handleVideoClick = (event: CustomEvent) => {
    if (
      event?.detail?.lastPlayInfo?.source === "user-event" ||
      event?.detail?.lastPauseInfo?.source === "user-event"
    ) {
      window.trackSolEvents?.(dlt, dld);
    }
  };

  const handleLoadedMediaData: WistiaProps["onLoadedMediaData"] = (event) => {
    setLoading(false);
    onLoadedMediaData?.(event);
  };

  const wistiaVideo = (
    <div
      ref={containerRef}
      className="focus-visible:outline-swdc-blue-400 relative size-full outline-none focus-visible:outline-2"
      style={{
        aspectRatio: !children
          ? (aspect_ratio ?? DEFAULT_ASPECT_RATIO)
          : undefined,
      }}
      {...getDataAutomationId("video")}
    >
      {!!children && (
        <div className="swdc-container bg-swdc-black-600 relative z-2 flex size-full flex-col items-center justify-center pt-14 pb-8 lg:hidden">
          {children}
        </div>
      )}
      {(shouldLoad || link) && (
        <WistiaMediaPlayer
          ref={playerRef}
          mediaId={uuid}
          onPlay={handleVideoClick}
          onPause={handleVideoClick}
          aspect={aspect_ratio ?? DEFAULT_ASPECT_RATIO}
          autoplay={(autoplay ?? children) ? true : undefined}
          silentAutoplay={(silent_auto_play ?? children) ? true : undefined}
          muted={children ? true : undefined}
          controlsVisibleOnLoad={children ? false : undefined}
          transparentLetterbox={children ? false : undefined}
          playBarControl={children ? false : undefined}
          audioDescriptionControl={children ? false : undefined}
          volumeControl={children ? false : undefined}
          fullscreenControl={children ? false : true}
          settingsControl={children ? false : undefined}
          bigPlayButton={children ? false : undefined}
          playPauseNotifier={children ? false : undefined}
          resumable={children ? false : undefined}
          endVideoBehavior={children ? "loop" : undefined}
          onLoadedMediaData={handleLoadedMediaData}
        />
      )}
      {!!children && (
        <>
          <div className="absolute inset-0 z-1 size-full">
            <div className="swdc-container hidden size-full flex-col items-center justify-center pt-14 pb-8 lg:flex">
              {children}
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-6 z-2">
            <div className="swdc-container hidden justify-end lg:flex">
              {!paused && (
                <button
                  onClick={pauseButtonHandler}
                  {...getDataLinkAttributes(video_play_pause_button_analytics)}
                  {...getDataAutomationId("video-pause-button")}
                  aria-label={video_pause_button_label}
                >
                  <PauseVideo className="text-swdc-black-600 hover:text-swdc-gray-400" />
                </button>
              )}
              {paused && (
                <button
                  onClick={playButtonHandler}
                  {...getDataLinkAttributes(video_play_pause_button_analytics)}
                  {...getDataAutomationId("video-play-button")}
                  aria-label={video_play_button_label}
                >
                  <PlayVideo className="text-swdc-black-600 hover:text-swdc-gray-400" />
                </button>
              )}
            </div>
            <div className="swdc-container flex justify-end lg:hidden">
              {!paused && (
                <button onClick={pauseButtonHandler}>
                  <PauseVideo
                    height={40}
                    width={40}
                    className="text-swdc-black-600 hover:text-swdc-gray-400"
                  />
                </button>
              )}
              {paused && (
                <button onClick={playButtonHandler}>
                  <PlayVideo
                    height={40}
                    width={40}
                    className="text-swdc-black-600 hover:text-swdc-gray-400"
                  />
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {link ? (
        <button
          type="button"
          tabIndex={0}
          onClick={openModal}
          {...getDataLinkAttributes({ dld, dlt })}
          {...getDataAutomationGroup("link")}
        >
          {link}
        </button>
      ) : (
        wistiaVideo
      )}
      <Transition
        show={isOpen}
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Dialog as="div" className="relative" onClose={closeModal}>
          <div className="bg-swdc-black/90 fixed inset-0" />
          <button
            aria-label="Close"
            className="fixed top-5 right-5 z-10 px-2 text-2xl text-white"
            onClick={closeModal}
          >
            <XMarkThick fill="currentColor" width={20} aria-hidden />
          </button>
          <div className="fixed top-0 left-1/2 h-screen w-full max-w-[calc((100vh-2.5%)*(16/9))] -translate-x-1/2 overflow-hidden p-[2.5%]">
            <DialogPanel
              className="relative top-1/2 -translate-y-1/2"
              {...getDataAutomationGroup("modal")}
            >
              {loading && (
                <div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  aria-hidden
                >
                  <Spinner className="size-20 animate-spin invert [animation-duration:2000ms]" />
                </div>
              )}
              <span role="status" aria-live="assertive" className="sr-only">
                {!loading && "Video is loaded"}
              </span>
              {wistiaVideo}
            </DialogPanel>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default WistiaPlayer;
